
import React, { useMemo, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUpDown, Search } from 'lucide-react';

export interface Column<T> {
  key: string;
  header: string;
  accessor: (row: T) => any;
  sortable?: boolean;
  filterable?: boolean;
  render?: (value: any, row: T) => React.ReactNode;
  width?: string;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

export interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  error?: string | null;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (page: number) => void;
  onSort?: (key: string, direction: 'asc' | 'desc') => void;
  onFilter?: (filters: Record<string, string>) => void;
  actions?: (row: T) => React.ReactNode;
  emptyState?: React.ReactNode;
  rowKey: (row: T) => string;
  selectable?: boolean;
  onSelectionChange?: (selected: T[]) => void;
  className?: string;
}

export function DataTable<T>({
  data,
  columns,
  loading = false,
  error = null,
  pageSize = 50,
  totalCount,
  onPageChange,
  onSort,
  onFilter,
  actions,
  emptyState,
  rowKey,
  selectable = false,
  onSelectionChange,
  className = '',
}: DataTableProps<T>) {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());

  // Calculate pagination
  const total = totalCount || data.length;
  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, total);

  // Sort and filter data (client-side if no server handlers)
  const processedData = useMemo(() => {
    let filtered = data;

    // Apply filters
    if (Object.keys(filters).length > 0) {
      filtered = filtered.filter(row => {
        return Object.entries(filters).every(([key, value]) => {
          const column = columns.find(c => c.key === key);
          if (!column) return true;
          const accessorValue = column.accessor(row);
          if (accessorValue === null || accessorValue === undefined) return value === '';
          const cellValue = String(accessorValue).toLowerCase();
          return cellValue.includes(value.toLowerCase());
        });
      });
    }

    // Apply sorting
    if (sortKey && !onSort) {
      const column = columns.find(c => c.key === sortKey);
      if (column) {
        filtered = [...filtered].sort((a, b) => {
          const aVal = column.accessor(a);
          const bVal = column.accessor(b);
          const multiplier = sortDirection === 'asc' ? 1 : -1;
          
          if (aVal < bVal) return -1 * multiplier;
          if (aVal > bVal) return 1 * multiplier;
          return 0;
        });
      }
    }

    return filtered;
  }, [data, filters, sortKey, sortDirection, columns, onFilter, onSort]);

  // Paginate
  const paginatedData = useMemo(() => {
    if (onPageChange) return data; // Server-side pagination
    return processedData.slice(startIndex, endIndex);
  }, [processedData, startIndex, endIndex, data, onPageChange]);

  const handleSort = useCallback((key: string) => {
    const newDirection = sortKey === key && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortKey(key);
    setSortDirection(newDirection);
    onSort?.(key, newDirection);
  }, [sortKey, sortDirection, onSort]);

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
    onPageChange?.(page);
    setSelectedRows(new Set()); // Clear selection on page change
  }, [onPageChange]);

  const handleFilterChange = useCallback((key: string, value: string) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      if (!value) delete newFilters[key];
      
      // Use set timeout to trigger onFilter to allow state update to settle
      setTimeout(() => {
        onFilter?.(newFilters);
      }, 0);
      
      return newFilters;
    });
    setCurrentPage(1);
  }, [onFilter]);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allKeys = new Set(paginatedData.map(rowKey));
      setSelectedRows(allKeys);
      onSelectionChange?.(paginatedData);
    } else {
      setSelectedRows(new Set());
      onSelectionChange?.([]);
    }
  }, [paginatedData, rowKey, onSelectionChange]);

  const handleSelectRow = useCallback((row: T, checked: boolean) => {
    const key = rowKey(row);
    const newSelected = new Set(selectedRows);
    
    if (checked) {
      newSelected.add(key);
    } else {
      newSelected.delete(key);
    }
    
    setSelectedRows(newSelected);
    const selectedData = paginatedData.filter(r => newSelected.has(rowKey(r)));
    onSelectionChange?.(selectedData);
  }, [selectedRows, paginatedData, rowKey, onSelectionChange]);

  // Error state
  if (error) {
    return (
      <div className="bg-red-900/20 border border-red-500 rounded-lg p-8 text-center">
        <div className="text-red-400 text-lg font-semibold mb-2">Error Loading Data</div>
        <div className="text-red-300 text-sm">{error}</div>
      </div>
    );
  }

  // Empty state
  if (!loading && paginatedData.length === 0) {
    return emptyState || (
      <div className="bg-gray-800 rounded-lg p-8 text-center">
        <div className="text-gray-400 text-lg">No data available</div>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                {selectable && (
                  <th className="px-4 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={selectedRows.size === paginatedData.length && paginatedData.length > 0}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4"
                      aria-label="Select all rows"
                    />
                  </th>
                )}
                {columns.map(column => (
                  <th
                    key={column.key}
                    className={`px-4 py-3 text-xs font-medium text-gray-300 ${
                      column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'
                    } ${column.width || ''}`}
                  >
                    <div className="flex items-center gap-2">
                      {column.header}
                      {column.sortable && (
                        <button
                          onClick={() => handleSort(column.key)}
                          className="hover:text-white transition-colors"
                          aria-label={`Sort by ${column.header}`}
                        >
                          <ArrowUpDown
                            size={14}
                            className={sortKey === column.key ? 'text-blue-400' : ''}
                          />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
                {actions && (
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-300">
                    Actions
                  </th>
                )}
              </tr>
              {/* Filter row */}
              <tr className="bg-gray-750">
                {selectable && <th className="px-4 py-2"></th>}
                {columns.map(column => (
                  <th key={`filter-${column.key}`} className="px-4 py-2">
                    {column.filterable && (
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" size={14} />
                        <input
                          type="text"
                          placeholder={`Filter ${column.header}`}
                          value={filters[column.key] || ''}
                          onChange={(e) => handleFilterChange(column.key, e.target.value)}
                          className="w-full pl-8 pr-2 py-1 bg-gray-800 border border-gray-600 rounded text-xs text-white placeholder-gray-500"
                          aria-label={`Filter by ${column.header}`}
                        />
                      </div>
                    )}
                  </th>
                ))}
                {actions && <th className="px-4 py-2"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                // Loading skeleton
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={`skeleton-${i}`} className="animate-pulse">
                    {selectable && <td className="px-4 py-3"><div className="h-4 w-4 bg-gray-700 rounded"></div></td>}
                    {columns.map(col => (
                      <td key={`skeleton-${i}-${col.key}`} className="px-4 py-3">
                        <div className="h-4 bg-gray-700 rounded w-3/4"></div>
                      </td>
                    ))}
                    {actions && <td className="px-4 py-3"><div className="h-4 bg-gray-700 rounded w-1/2 mx-auto"></div></td>}
                  </tr>
                ))
              ) : (
                paginatedData.map(row => {
                  const key = rowKey(row);
                  const isSelected = selectedRows.has(key);
                  
                  return (
                    <tr
                      key={key}
                      className={`hover:bg-gray-750 transition-colors ${isSelected ? 'bg-blue-900/20' : ''}`}
                    >
                      {selectable && (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => handleSelectRow(row, e.target.checked)}
                            className="w-4 h-4"
                            aria-label={`Select row ${key}`}
                          />
                        </td>
                      )}
                      {columns.map(column => {
                        const value = column.accessor(row);
                        return (
                          <td
                            key={`${key}-${column.key}`}
                            className={`px-4 py-3 text-sm ${
                              column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'
                            } ${column.className || 'text-gray-300'}`}
                          >
                            {column.render ? column.render(value, row) : value}
                          </td>
                        );
                      })}
                      {actions && (
                        <td className="px-4 py-3 text-center">
                          {actions(row)}
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-400">
            Showing {startIndex + 1} to {endIndex} of {total} results
            {selectedRows.size > 0 && ` (${selectedRows.size} selected)`}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="First page"
            >
              <ChevronsLeft size={18} />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Previous page"
            >
              <ChevronLeft size={18} />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`px-3 py-1 rounded ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'hover:bg-gray-700 text-gray-300'
                    }`}
                    aria-label={`Page ${pageNum}`}
                    aria-current={currentPage === pageNum ? 'page' : undefined}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Next page"
            >
              <ChevronRight size={18} />
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 rounded hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Last page"
            >
              <ChevronsRight size={18} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
